/**
 * Página de Monitoramento da Fila Financeira PGMQ
 * 
 * Dashboard completo para observabilidade do pipeline de pagamentos
 * (webhooks financeiros + processamento PGMQ), incluindo métricas,
 * gráficos de volume, alertas de saúde e tabela de itens.
 */

import { useState } from 'react';
import { RefreshCw, DollarSign, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { FinancialQueueStatusCards } from '@/components/dashboard/FinancialQueueStatusCards';
import { FinancialQueueVolumeChart } from '@/components/dashboard/FinancialQueueVolumeChart';
import { FinancialQueueHealthAlerts } from '@/components/dashboard/FinancialQueueHealthAlerts';
import { FinancialQueueItemsTable } from '@/components/dashboard/FinancialQueueItemsTable';
import { FinancialQueueSlowestItems } from '@/components/dashboard/FinancialQueueSlowestItems';

export default function FinancialQueueMonitor() {
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Função para atualizar todos os dados
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['financial-queue-stats'] }),
      queryClient.invalidateQueries({ queryKey: ['financial-queue-items'] }),
      queryClient.invalidateQueries({ queryKey: ['financial-queue-volume'] }),
      queryClient.invalidateQueries({ queryKey: ['financial-queue-alerts'] }),
      queryClient.invalidateQueries({ queryKey: ['financial-queue-slowest'] }),
    ]);
    setLastUpdate(new Date());
    setIsRefreshing(false);
  };

  // Formatar tempo desde última atualização
  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `há ${minutes}min`;
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Fila Financeira</h1>
            <p className="text-muted-foreground">
              Monitoramento do pipeline de webhooks financeiros e processamento PGMQ
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Atualizado {getTimeSinceUpdate()}</span>
          </div>
          <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Status */}
      <FinancialQueueStatusCards />

      {/* Alertas de Saúde */}
      <FinancialQueueHealthAlerts />

      {/* Gráfico de Volume */}
      <div className="grid gap-6 md:grid-cols-1">
        <FinancialQueueVolumeChart />
      </div>

      {/* Top 20 mais lentos */}
      <FinancialQueueSlowestItems />

      {/* Tabela de Itens */}
      <FinancialQueueItemsTable />
    </div>
  );
}
